import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import config from 'const/config'
import routes from 'const/routes'
import Container from 'pages/subcomponents'
import i18n from 'strings/i18n'
import styled from 'styled-components'
import query from 'syf-component-library/const/mediaQueries'
import { spacing } from 'syf-component-library/defaultTheme'
import { Button as BrandRefreshButton } from '@syf-cl/button'
import CLButton from 'syf-component-library/ui/atoms/Button/Button'
import Card from 'syf-component-library/ui/atoms/Card/Card'
import { Inset, Stack } from 'syf-component-library/ui/spacing'
import { Body, H4, H5 } from 'syf-component-library/ui/typography'
import { handleMakeAPaymentOffline } from 'helpers/handleMakeAPaymentOffline'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'

export const RainCloudsIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.cyanBlue};
  height: 61px;
`

export const TextWrapper = styled.div<{ hasTechnicalError: boolean }>`
  margin: 0 auto;
  text-align: center;
  max-width: ${({ hasTechnicalError }) =>
    hasTechnicalError ? '656px' : '492px'};

  @media ${query.lessThanSmall} {
    max-width: 267px;
  }
`

export const ErrorCard = styled(Card)<{ hasTechnicalError: boolean }>`
  width: 702px;
  padding: ${({ hasTechnicalError }) =>
    hasTechnicalError ? `${spacing.xxlarge}px 56px` : '0'};

  @media ${query.betweenSmallMedium} {
    width: 430px;
  }

  @media ${query.greaterThanLarge} {
    width: ${({ hasTechnicalError }) =>
      hasTechnicalError ? '768px' : '702px'};
  }

  @media ${query.lessThanSmall} {
    width: ${({ hasTechnicalError }) =>
      hasTechnicalError ? '343px' : '315px'};
  }
`

export const ErrorWrapper = styled(Inset)`
  margin: auto;
`

export const DisabledCookiesError = (): ReactElement => {
  return (
    <Inset top="large" bottom="xxlarge">
      <H5 as="h3">
        Please change your browser settings to allow cookies or choose a
        different browser to access the account manager.
      </H5>
    </Inset>
  )
}

export const NoAccountError = (): ReactElement => {
  return (
    <>
      <H5 as="h3">{i18n({ errors: 'unableToFindAccount' })}</H5>
      <Stack size="small" hideOnBreakpointDown="large" />
      <Inset top="large" bottom="xxlarge">
        <H5 as="h4">
          {i18n({ errors: 'accountClosedOrBlocked' })} &nbsp;
          {i18n({ errors: 'contactCustomerService' })}
        </H5>
      </Inset>
    </>
  )
}

export const TryAgainButtonLegacy = styled(CLButton)`
  margin: auto;
  padding: 0 ${spacing.xxlarge}px;
`

export const MakeAPaymentOfflineButtonLegacy = styled(CLButton)`
  margin: auto;
  padding: ${spacing.medium}px ${spacing.large}px;
  @media ${query.lessThanSmall} {
    border-width: 0px;
    width: 235px;
  }
`

export const MakeAPaymentOfflineButtonForLWSLegacy = styled(
  MakeAPaymentOfflineButtonLegacy
)`
  @media ${query.lessThanSmall} {
    border-width: 2px;
    width: auto;
  }
`

export const TechnicalErrorImageWrapper = styled.div`
  width: 216px;
  height: 144px;
  margin: 0 auto;
  @media ${query.lessThanMedium} {
    width: 144px;
    height: 96px;
  }
`

export const TechnicalErrorImage = styled.img`
  width: 100%;
  height: 100%;
`

export const TechnicalDifficultiesError = (): ReactElement => {
  const brandRefreshEnabled = isBrandRefreshEnabled()
  const OfflineButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : MakeAPaymentOfflineButtonLegacy
  const variantProps = brandRefreshEnabled
    ? {
        variant: 'primary',
        'data-cy': 'make-a-payment-offline-button',
        style: { textTransform: 'uppercase' as const }
      }
    : {
        buttonType: 'primary',
        dataCy: 'make-a-payment-offline-button',
        'data-object': 'button'
      }

  return (
    <>
      <Body data-test="technical-error-header">
        {i18n({ errors: 'technicalDifficulties' })}{' '}
        {i18n({ errors: 'tryAgainLater' })}
      </Body>
      <Stack size="xlarge" hideOnBreakpointDown="small" />
      <Stack size="large" hideOnBreakpointUp="small" />
      <OfflineButton
        {...variantProps}
        data-analytics="clicktrackingevent"
        data-reason="still looking to make a payment?"
        data-test="down-time-pay"
        data-type="downtime pay | technical difficulty"
        onClick={handleMakeAPaymentOffline}
      >
        {i18n({ button: 'makeAPaymentOffline' })}
      </OfflineButton>
    </>
  )
}

export const FullPageError = (): ReactElement => {
  const navigate = useNavigate()
  const brandRefreshEnabled = isBrandRefreshEnabled()
  const RetryButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : TryAgainButtonLegacy
  const variantProps = brandRefreshEnabled
    ? { variant: 'primary', style: { textTransform: 'uppercase' as const } }
    : { buttonType: 'primary', 'data-object': 'button' }

  return (
    <>
      <H5 as="h3">{i18n({ errors: 'technicalDifficulties' })}</H5>
      <Stack size="small" hideOnBreakpointDown="large" />
      <Inset top="large" bottom="xxlarge">
        <RetryButton
          {...variantProps}
          data-type="error"
          data-reason="try again"
          data-test="try-again"
          data-analytics="clicktrackingevent"
          onClick={() => navigate(routes.HOME)}
        >
          {i18n({ errors: 'tryAgain' })}
        </RetryButton>
      </Inset>
    </>
  )
}

export const InnerContainer = styled.div`
  text-align: center;
  width: 100%;
  position: relative;

  @media ${query.greaterThanMedium} {
    width: 768px;
  }
`

const ErrorHeader = styled.div`
  background-color: ${({ theme }) => theme.yellow};
  height: 256px;
  width: 100%;

  @media ${query.lessThanMedium} {
    height: 166px;
  }

  img {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: 0;
    max-width: 100%;

    @media ${query.lessThanMedium} {
      height: 178px;
    }

    @media ${query.greaterThanLarge} {
      height: 256px;
    }
  }
`

export const FullPageRewardsError = (): ReactElement => {
  const navigate = useNavigate()
  const brandRefreshEnabled = isBrandRefreshEnabled()
  const RetryButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : TryAgainButtonLegacy
  const variantProps = brandRefreshEnabled
    ? { variant: 'primary', style: { textTransform: 'uppercase' } }
    : { buttonType: 'primary', 'data-object': 'button' }

  return (
    <Container>
      <InnerContainer>
        <Stack size="medium" hideOnBreakpointUp="medium" />
        <Stack size="large" hideOnBreakpointDown="medium" />
        <Card>
          <ErrorHeader>
            <img
              src={`${config.ASSETS_HOST}/assets/vista/error_cat.svg`}
              alt={`${i18n({ logout: 'pageTitle' })}`}
            />
          </ErrorHeader>
          <Inset horizontal="xxlarge" vertical="base">
            <H4 as="h3" weight="bold">
              {i18n({ overlayMessages: 'careCreditUnavailable' })}
            </H4>
            <Stack size="xsmall" />
            <H5 as="h4">{i18n({ overlayMessages: 'tryLater' })}</H5>
            <Stack size="large" />
            <RetryButton
              {...variantProps}
              data-type="error"
              data-reason="return to account manager"
              data-analytics="clicktrackingevent"
              onClick={() => navigate(routes.HOME)}
            >
              {i18n({ button: 'accountManagerReturn' })}
            </RetryButton>
          </Inset>
        </Card>
      </InnerContainer>
    </Container>
  )
}
